import React, { memo, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import ReactTooltip from 'react-tooltip';

import { QuestionAutofill } from 'src/components/JobDatasheet/QuestionAutofill';
import { QuestionCheckbox } from 'src/components/JobDatasheet/QuestionCheckbox';
import { QuestionCurrency } from 'src/components/JobDatasheet/QuestionCurrency';
import { QuestionDate } from 'src/components/JobDatasheet/QuestionDate';
import { QuestionDropdown } from 'src/components/JobDatasheet/QuestionDropdown';
import { QuestionDependentDropdown } from 'src/components/JobDatasheet/QuestionDependentDropdown';
import { QuestionFile } from 'src/components/JobDatasheet/QuestionFile';
import { QuestionMonth } from 'src/components/JobDatasheet/QuestionMonth';
import { QuestionNumber } from 'src/components/JobDatasheet/QuestionNumber';
import { QuestionPercent } from 'src/components/JobDatasheet/QuestionPercent';
import { QuestionRadioButton } from 'src/components/JobDatasheet/QuestionRadioButton';
import { QuestionRelation } from 'src/components/JobDatasheet/QuestionRelation';
import { QuestionSmileyRating } from 'src/components/JobDatasheet/QuestionSmileyRating';
import { QuestionStarRating } from 'src/components/JobDatasheet/QuestionStarRating';
import { QuestionTable } from 'src/components/JobDatasheet/QuestionTable';
import { QuestionText } from 'src/components/JobDatasheet/QuestionText';
import { QuestionTime } from 'src/components/JobDatasheet/QuestionTime';
import { QuestionYear } from 'src/components/JobDatasheet/QuestionYear';
import { popupAlert } from 'src/components/Popup';
import s from 'src/components/JobDatasheet/JobDatasheetForm/JobDatasheetForm.styles';
import { noop } from 'src/utils/core/event';
import JobDatasheetLanguageContext from 'src/components/JobDatasheet/context/JobDatasheetLanguageContext';
import { VERIFIER_REVIEW_UNDERWAY, REVIEWER_REVIEW_UNDERWAY } from 'src/utils/data/job';
import Svg from 'src/components/Svg';
import { isMandatoryQuestion } from './utils';

const questionTypeMapping = {
  autofill: QuestionAutofill,
  checkbox: QuestionCheckbox,
  currency: QuestionCurrency,
  date: QuestionDate,
  dropdown: QuestionDropdown,
  dependentDropdown: QuestionDependentDropdown,
  file: QuestionFile,
  month: QuestionMonth,
  number: QuestionNumber,
  percent: QuestionPercent,
  radioButton: QuestionRadioButton,
  relation: QuestionRelation,
  smileyRating: QuestionSmileyRating,
  starRating: QuestionStarRating,
  table: QuestionTable,
  text: QuestionText,
  time: QuestionTime,
  year: QuestionYear,
};

const QuestionRowComponent = ({
  fromJob,
  getValidationErrors,
  isAdditionalReportingDocuments,
  isBlockSaveFailed,
  isBlockSaveSuccess,
  isFromCustomPanel,
  isPrefilled,
  getNumberOfAttachments,
  job,
  parentOfActiveQuestion,
  isRectifiedQuestion,
  question,
  readonly,
  result,
  sectionOriginalIndex,
  selectedQuestion,
  setResult,
  statistic,
  toggleSelectedQuestion,
  isInternalUser,
  getResult,
  allQuestions,
  isQuesPresentInMultipleDatasheets,
  toggleOpenComment,
  showError,
  activeQuestion
}) => {
  const { t } = useTranslation();

  const { selectedLanguage, availableLanguages, highlightMissing = false } = useContext(JobDatasheetLanguageContext);

  let questionCurrentVersion = question?.datasheetQuestion?.currentVersion;
  const isActionRequired =
    statistic && statistic.actionRequiredtypeQuestionIds.includes(question.id);
  const {
    descriptionAlias,
    descriptionExternal,
    questionType,
  } = questionCurrentVersion;
  const isLoadingDatePicker = !isRectifiedQuestion && ( questionType === 'date' || questionType === 'month' || questionType === 'year');
  let descriptionToShow = isInternalUser ? descriptionAlias : descriptionExternal;

  if(questionType === 'radioButton'){
    questionCurrentVersion = {...questionCurrentVersion, validations: question?.validations};
  }

  const QuestionTypeForm = questionTypeMapping[questionType];
  if (!QuestionTypeForm) {
    return null;
  }

  const isMandatory= isMandatoryQuestion(question);
  const validationErrors = isRectifiedQuestion ? [] : (getValidationErrors(question) || []);
  const errors = validationErrors;
  if (isBlockSaveFailed?.(question.datasheetQuestionId)) {
    errors.push(t('ALERT_SAVE_FAILED'));
  }

  const fullSize =
    questionType === 'table' &&
    questionCurrentVersion?.tableSettings?.maximizeWidth;
  const textFullSize =
    questionType === 'text' &&
    questionCurrentVersion?.textSettings?.isMaximized;

  const attachmentCounter = getNumberOfAttachments(question);

  let questionText = questionCurrentVersion?.text;
  let isLanguageHighlighted = false;
  if (selectedLanguage) {
    const questionTranslation = (questionCurrentVersion?.translations || []).find(c => c.languageId === selectedLanguage);
    questionText = questionTranslation?.text || questionText;
    descriptionToShow = questionTranslation?.descriptionAlias || descriptionToShow;
    if (!questionTranslation) {
      isLanguageHighlighted = highlightMissing;
    }
  }

  const setLinkStatus = (res, isLinkedToSimilar) => {

    let resultWithLinkingState;
    if (!res) {
      resultWithLinkingState = { isLinkedToSimilar };
    } else {
      resultWithLinkingState = { ...res, isLinkedToSimilar };
    }
    setResult(question, 'result', resultWithLinkingState);
  };

  const showLinkStatusPopup = (res, isLinkedToSimilar) => {
    let isLinked = false;
    const linkedStatus = getResult(question)?.result?.isLinkedToSimilar;
    if (linkedStatus === undefined || linkedStatus) {
      isLinked = true;
    }
    popupAlert({
      body: isLinked ? t('QUESTION_UNLINK_POPUP_TEXT') : t('QUESTION_LINK_POPUP_TEXT'),
      onSubmit: () => setLinkStatus(res, isLinkedToSimilar),
      hasCancel: true,
    });
  };

  const showQuestionLinkedMarkers = (res = {}) => {

    if (isQuesPresentInMultipleDatasheets()) {
      // checking undefined for the existence of property
      if (res?.isLinkedToSimilar === undefined || res?.isLinkedToSimilar) {
        return <Svg name="linked" onClick={() => showLinkStatusPopup(res, false)}
                    style={{ width: '26px', marginLeft: '-7px', paddingTop: '2px', cursor: 'pointer' }} />;
      } if (typeof res?.isLinkedToSimilar === 'boolean' && !res?.isLinkedToSimilar) {
        return <Svg name="unlinked" onClick={() => showLinkStatusPopup(res, true)}
                    style={{ width: '26px', marginLeft: '-7px', paddingTop: '2px', cursor: 'pointer' }} />;
      }
    }
    return null;
  };

  const preResultChange = (ques, field, value, shouldShowError) => {
    let isLinked = false;
    const linkedStatus = getResult(question)?.result?.isLinkedToSimilar;
    if (linkedStatus === undefined || linkedStatus) {
      isLinked = true;
    }
    if (isQuesPresentInMultipleDatasheets()) {
      if (isLinked) {
        setResult(ques, field, { ...value, isLinkedToSimilar: true });
        return;
      } if (!isLinked) {
        setResult(ques, field, { ...value, isLinkedToSimilar: false });
        return;
      }
    }
    setResult(question, 'result', value, shouldShowError);
  };

  const linkUnlinkTooltipText = () => {
    let isLinked = false;
    const linkedStatus = getResult(question)?.result?.isLinkedToSimilar;
    if (linkedStatus === undefined || linkedStatus) {
      isLinked = true;
    }
    return isLinked ? t('QUESTION_MULTIPLE_TIMES_LINKED') : t('QUESTION_MULTIPLE_TIMES_UNLINKED');
  };

  const showResultVerificationSection = !toggleOpenComment || (toggleOpenComment && getResult(question)?.commentStatus === 'open');
  const hideMandatoryErrorQuestionType = ['table', 'checkbox', 'radioButton', 'file', 'picture', 'smileyRating', 'starRating'];
  const mandatoryText = !hideMandatoryErrorQuestionType.includes(questionType) ?  t('MANDATORY') : '';
  const isQuestionHighlighted = activeQuestion?.id === question?.id && [VERIFIER_REVIEW_UNDERWAY, REVIEWER_REVIEW_UNDERWAY].includes(job?.status);
  const highlightOnHover = [VERIFIER_REVIEW_UNDERWAY, REVIEWER_REVIEW_UNDERWAY].includes(job?.status);

  return (
    <s.QuestionRowWrapper 
      className={` ${!showResultVerificationSection && 'cursor-auto'} ${isQuestionHighlighted && 'highlight-question'} ${highlightOnHover && 'highlight-on-hover'}`}
    >
      <ReactTooltip place="bottom"/>
      <s.QuestionWrapper
        isFromCustomPanel={isFromCustomPanel}
        isRectifiedQuestion={isRectifiedQuestion}
        onClick={() => showResultVerificationSection && !isRectifiedQuestion && !isFromCustomPanel ? toggleSelectedQuestion(
          sectionOriginalIndex,
          question.position,
        ) : noop()}
        selected={
          selectedQuestion &&
          selectedQuestion.section === sectionOriginalIndex &&
          selectedQuestion.question === question.position
        }
        textFullSize={textFullSize}
        type={questionType}
      >
        {isActionRequired && <s.RedDot middle type={questionType} />}
        <s.QuestionLabel
          fullsize={questionType === 'checkbox' || isRectifiedQuestion || isFromCustomPanel}
          onOwnLine={fullSize || textFullSize}
        >
          {questionType === 'checkbox' ? (
              <s.QuestionLinkageStatusWrapper>
                <span data-tip={linkUnlinkTooltipText()}>{showQuestionLinkedMarkers(result)}</span>
            <QuestionTypeForm
              fromJob={fromJob}
              isRectifiedQuestion={isRectifiedQuestion}
              job={job}
              question={questionCurrentVersion}
              label={(
                <div>
                  <s.QuestionTextLabel isLanguageHighlighted={isLanguageHighlighted} isMandatory={isMandatory} isError={showError && errors.length>0}>
                    {isRectifiedQuestion ? t('RECTIFIED_ANSWER_BY_ASSESSOR') : `${question.questionNumber ? `${question.questionNumber} ${questionText}` : questionText}`}
                  </s.QuestionTextLabel>
                  {descriptionToShow && <s.DescriptionAlias>{descriptionToShow}</s.DescriptionAlias>}
                </div>
              )}
              onChange={(value, shouldShowError = true) => preResultChange(question, 'result', value, shouldShowError)}
              readonly={readonly}
              value={result}
            />
              </s.QuestionLinkageStatusWrapper>
          ) : (
            <s.QuestionLinkageStatusWrapper>
                <span data-tip={linkUnlinkTooltipText()}>{showQuestionLinkedMarkers(result)}</span>
            <div>
              <s.QuestionTextLabel isLanguageHighlighted={isLanguageHighlighted} isMandatory={isMandatory} isError={showError && errors.length>0}>
                {isRectifiedQuestion ? t('RECTIFIED_ANSWER_BY_ASSESSOR') :                   
                  `${question.questionNumber ? `${question.questionNumber} ${questionText}` : questionText}`}
              </s.QuestionTextLabel>
              {descriptionToShow && <s.DescriptionAlias>{descriptionToShow}</s.DescriptionAlias>}
            </div>
            </s.QuestionLinkageStatusWrapper>
          )}
        </s.QuestionLabel>
        {questionType !== 'checkbox' && (
          <s.QuestionTypeFormContainer>
            <s.QuestionTypeFormContainerInner hasAttachments={attachmentCounter > 0} isLoadingDatePicker={isLoadingDatePicker}>
              <QuestionTypeForm
                fromJob={fromJob}
                isAdditionalReportingDocuments={isAdditionalReportingDocuments}
                isRectifiedQuestion={isRectifiedQuestion}
                job={job}
                onChange={(value, shouldShowError = false) => preResultChange(question, 'result', value, shouldShowError)}
                parentOfActiveQuestion={parentOfActiveQuestion}
                question={questionCurrentVersion}
                readonly={readonly}
                value={result}
                getResult={getResult}
                allQuestions={allQuestions}
                errors={questionType === 'table' ? errors : null}
                showError={showError}
                isSurvey={false}
              />
            </s.QuestionTypeFormContainerInner>
            {
              attachmentCounter > 0 &&
              <s.QuestionAttachmentCountContainer>
                <s.AttachmentsCountIcon name="attachment-pin" />
                <div className="attachment-counter">{attachmentCounter}</div>
              </s.QuestionAttachmentCountContainer>
            }
          </s.QuestionTypeFormContainer>
        )}
        {showError && errors.length>0 && questionType !== 'table' &&(
            <s.ErrorWrapper>
              {errors.includes("mandatory") ? mandatoryText : errors}
            </s.ErrorWrapper>
          )
        }
      </s.QuestionWrapper>
      {isRectifiedQuestion && (availableLanguages.length > 1) && (
        <s.QuestionWrapper
          isFromCustomPanel={isFromCustomPanel}
          isRectifiedQuestion
          onClick={noop}
          textFullSize={textFullSize}
          type={questionType}
        >
          <s.QuestionLabel
            fullsize
            onOwnLine={fullSize || textFullSize}
          >
            <span>
              {t('RECTIFIED_ANSWER_BY_ASSESSOR_ENGLISH')}
            </span>
          </s.QuestionLabel>
          <s.QuestionTypeFormContainer>
            <s.QuestionTypeFormContainerInner>
              <QuestionText
                isRectifiedQuestion
                onChange={(value, shouldShowError = false) => {
                  setResult(question, 'result', {
                    ...value,
                    rectifiedValue: value.originalRectifiedValue,
                    translatedAnswer: value.rectifiedValue,
                  }, shouldShowError);
                }}
                question={questionCurrentVersion}
                readonly={readonly}
                value={{
                  ...(result || {}),
                  originalRectifiedValue: result?.rectifiedValue || '',
                  rectifiedValue: result?.translatedAnswer || '',
                }}
                questionType= {question.datasheetQuestion.currentVersion.questionType}
              />
            </s.QuestionTypeFormContainerInner>
          </s.QuestionTypeFormContainer>
        </s.QuestionWrapper>
      )}
    </s.QuestionRowWrapper>
  );
};

QuestionRowComponent.defaultProps = {
  getNumberOfAttachments: noop,
  getValidationErrors: () => noop(),
  isQuesPresentInMultipleDatasheets: noop,
  setResult: () => noop(),
};

export const QuestionRow = memo(QuestionRowComponent);
